import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../App";
import { toast } from "sonner";
import {
  Building2, LogOut, Plus, FileText, MessageSquare, ClipboardCheck,
  DollarSign, Settings, Bell, User, Star, Sparkles, Leaf, Waves, Wind, Wrench,
  ChevronRight, Clock, CheckCircle, TrendingUp, Briefcase
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  cat_diarista: Sparkles,
  cat_jardinagem: Leaf,
  cat_piscina: Waves,
  cat_arcondicionado: Wind,
  cat_reforma: Wrench
};

export default function ProviderDashboard() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };

        const [statsRes, requestsRes, budgetsRes, contractsRes] = await Promise.all([
          fetch(`${API_URL}/api/dashboard/stats`, { headers }),
          fetch(`${API_URL}/api/services/requests`, { headers }),
          fetch(`${API_URL}/api/budgets`, { headers }),
          fetch(`${API_URL}/api/contracts`, { headers })
        ]);

        if (statsRes.ok) setStats(await statsRes.json());
        if (requestsRes.ok) setRequests(await requestsRes.json());
        if (budgetsRes.ok) setBudgets(await budgetsRes.json());
        if (contractsRes.ok) setContracts(await contractsRes.json());
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        toast.error("Erro ao carregar dados");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="provider-dashboard">
      {/* Header */}
      <header className="bg-slate-900 text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-slate-900" />
              </div>
              <span className="text-xl font-bold hidden sm:inline">eZelo.</span>
              <Badge variant="secondary" className="ml-2 bg-emerald-500 text-white border-0">
                PRO
              </Badge>
            </Link>

            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" className="text-white relative">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-emerald-400 rounded-full"></span>
              </Button>
              
              <Button variant="ghost" className="flex items-center gap-2 text-white" onClick={() => navigate("/profile")}>
                <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                  ) : (
                    <User className="w-4 h-4" />
                  )}
                </div>
                <span className="hidden sm:inline text-sm font-medium">{user?.name}</span>
              </Button>

              <Button variant="ghost" size="icon" onClick={handleLogout} className="text-white" data-testid="logout-btn">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
            Painel do Prestador
          </h1>
          <p className="text-slate-600">Gerencie seus serviços e acompanhe seus ganhos</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Ganho</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    R$ {(stats?.total_earned || 0).toLocaleString("pt-BR")}
                  </p>
                </div>
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Avaliação</p>
                  <p className="text-2xl font-bold text-amber-500">
                    {(stats?.average_rating || 0).toFixed(1)} <Star className="w-5 h-5 inline text-amber-400" />
                  </p>
                </div>
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Star className="w-5 h-5 text-amber-500" />
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-1">{stats?.total_reviews || 0} avaliações</p>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Serviços Ativos</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.active_contracts || 0}</p>
                </div>
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Orçamentos Pendentes</p>
                  <p className="text-2xl font-bold text-amber-600">{stats?.pending_budgets || 0}</p>
                </div>
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Profile Setup Alert */}
        {stats?.message && (
          <Card className="mb-8 border-amber-200 bg-amber-50">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <Settings className="w-6 h-6 text-amber-600 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Complete seu perfil</h3>
                    <p className="text-slate-600 text-sm">Configure suas categorias e informações para receber solicitações</p>
                  </div>
                </div>
                <Button onClick={() => navigate("/profile")} data-testid="setup-profile-btn">
                  Configurar Perfil
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Content Grid */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Available Requests */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">Solicitações Disponíveis</CardTitle>
              <Badge variant="secondary">{requests.length} novas</Badge>
            </CardHeader>
            <CardContent>
              {requests.length === 0 ? (
                <div className="text-center py-8">
                  <ClipboardCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Nenhuma solicitação disponível</p>
                  <p className="text-xs text-slate-400">Configure suas categorias no perfil</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {requests.slice(0, 5).map((request) => {
                    const Icon = CATEGORY_ICONS[request.category_id] || FileText;
                    return (
                      <div 
                        key={request.request_id}
                        className="p-4 border border-slate-200 rounded-lg hover:border-emerald-300 hover:bg-emerald-50/50 transition-colors cursor-pointer"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Icon className="w-5 h-5 text-slate-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{request.title}</p>
                              <p className="text-sm text-slate-500 line-clamp-1">{request.description}</p>
                              <p className="text-xs text-slate-400 mt-1">
                                {request.address?.city}, {request.address?.state}
                              </p>
                            </div>
                          </div>
                          <Button size="sm" className="flex-shrink-0" data-testid={`create-budget-${request.request_id}`}>
                            Orçar
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* My Budgets */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">Meus Orçamentos</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate("/budgets")}>
                Ver todos
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              {budgets.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Nenhum orçamento enviado</p>
                  <p className="text-xs text-slate-400">Envie orçamentos para as solicitações disponíveis</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {budgets.slice(0, 5).map((budget) => {
                    const Icon = CATEGORY_ICONS[budget.category_id] || FileText;
                    return (
                      <div 
                        key={budget.budget_id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <Icon className="w-5 h-5 text-slate-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 text-sm">
                              R$ {budget.total?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                            </p>
                            <p className="text-xs text-slate-500">
                              {new Date(budget.created_at).toLocaleDateString("pt-BR")}
                            </p>
                          </div>
                        </div>
                        <Badge 
                          variant={budget.status === "accepted" ? "default" : "secondary"}
                          className={budget.status === "accepted" ? "bg-emerald-100 text-emerald-700" : ""}
                        >
                          {budget.status === "pending" ? "Aguardando" : budget.status === "accepted" ? "Aceito" : budget.status}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Active Contracts */}
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">Meus Serviços em Andamento</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate("/contracts")}>
                Ver todos
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              {contracts.filter(c => c.status === "active").length === 0 ? (
                <div className="text-center py-8">
                  <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Nenhum serviço em andamento</p>
                  <p className="text-xs text-slate-400">Seus contratos ativos aparecerão aqui</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {contracts.filter(c => c.status === "active").slice(0, 4).map((contract) => {
                    const Icon = CATEGORY_ICONS[contract.category_id] || FileText;
                    return (
                      <div 
                        key={contract.contract_id}
                        className="p-4 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors cursor-pointer"
                        onClick={() => navigate("/contracts")}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <Icon className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">Contrato #{contract.contract_id.slice(-8)}</p>
                            <p className="text-xs text-slate-500">
                              R$ {contract.total_value?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <Badge className="bg-emerald-100 text-emerald-700">
                            Em Andamento
                          </Badge>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
